"use client"

import { useEffect, useState } from "react"
import { collection, getDocs } from "firebase/firestore"
import { db } from "../firebase"
import {
  FaUsers,
  FaPalette,
  FaDesktop,
  FaSpinner,
  FaExclamationTriangle,
  FaQuoteLeft,
  FaCamera,
  FaVideo,
  FaStar,
  FaMagic,
  FaPaintBrush,
} from "react-icons/fa"

const GraphicsandDesign = () => {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [visibleSections, setVisibleSections] = useState(new Set())
  const [activeCard, setActiveCard] = useState(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]))
          }
        })
      },
      { threshold: 0.1 },
    )

    document.querySelectorAll("[data-animate]").forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const fetchGraphicsMembers = async () => {
      try {
        setLoading(true)
        const snapshot = await getDocs(collection(db, "committees/Graphics and Design/members"))
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
       // Sort by order field, then by designation priority (Head first, Co-Head second)
        const sortedData = data.sort((a, b) => {
          // First sort by order if it exists
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order
          }
          if (a.order !== undefined) return -1
          if (b.order !== undefined) return 1

          // Then sort by designation priority
          const designationPriority = { Head: 0, "Co-Head": 1 }
          const aPriority = designationPriority[a.designation] ?? 2
          const bPriority = designationPriority[b.designation] ?? 2

          if (aPriority !== bPriority) return aPriority - bPriority

          // Finally sort by name
          return a.name.localeCompare(b.name)
        })

        setMembers(sortedData)
      } catch (error) {
        console.error("Error fetching Graphics and Design members:", error)
        setError("Failed to load committee members")
      } finally {
        setLoading(false)
      }
    }

    fetchGraphicsMembers()
  }, [])

  const highlights = [
    {
      icon: FaPaintBrush,
      title: "Creative Design",
      description: "Crafting stunning visuals and brand identity",
      color: "from-purple-500 to-purple-700",
      bgPattern: "bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20",
    },
    {
      icon: FaCamera,
      title: "Photography",
      description: "Capturing moments and creating visual stories",
      color: "from-pink-500 to-pink-700",
      bgPattern: "bg-gradient-to-br from-pink-50 to-pink-100 dark:from-pink-900/20 dark:to-pink-800/20",
    },
    {
      icon: FaVideo,
      title: "Video Production",
      description: "Professional video editing and live streaming",
      color: "from-rose-500 to-rose-700",
      bgPattern: "bg-gradient-to-br from-rose-50 to-rose-100 dark:from-rose-900/20 dark:to-rose-800/20",
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/30 to-pink-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full mix-blend-multiply filter blur-3xl animate-float"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-pink-400/20 to-purple-400/20 rounded-full mix-blend-multiply filter blur-3xl animate-float-delayed"></div>
        <div className="absolute top-1/4 left-1/3 w-80 h-80 bg-gradient-to-r from-rose-400/10 to-purple-400/10 rounded-full mix-blend-multiply filter blur-2xl animate-pulse"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        {/* Creative Header */}
        <div
          data-animate
          id="header"
          className={`text-center mb-20 transform transition-all duration-1000 ${
            visibleSections.has("header") ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0"
          }`}
        >
          <div className="relative inline-block mb-8">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full blur-lg opacity-30 animate-pulse"></div>
            <div className="relative w-24 h-24 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center shadow-2xl">
              <FaPalette className="w-12 h-12 text-white" />
            </div>
          </div>

          <h1 className="text-6xl md:text-8xl font-black mb-6 relative">
            <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 bg-clip-text text-transparent animate-gradient-x">
              Graphics &
            </span>
            <br />
            <span className="bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 bg-clip-text text-transparent animate-gradient-x-reverse">
              Design
            </span>
          </h1>

          <div className="flex justify-center items-center space-x-4 mb-8">
            <div className="w-16 h-1 bg-gradient-to-r from-purple-500 to-transparent rounded-full"></div>
            <FaStar className="w-6 h-6 text-yellow-500 animate-spin-slow" />
            <div className="w-16 h-1 bg-gradient-to-l from-pink-500 to-transparent rounded-full"></div>
          </div>

          <p className="text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed font-light">
            Where creativity meets innovation in visual storytelling and artistic excellence
          </p>
        </div>

        {/* Creative Highlights */}
        <div
          data-animate
          id="highlights"
          className={`mb-20 transform transition-all duration-1000 delay-200 ${
            visibleSections.has("highlights") ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0"
          }`}
        >
          <div className="grid md:grid-cols-3 gap-8">
            {highlights.map((highlight, index) => (
              <div
                key={index}
                className={`group relative overflow-hidden rounded-3xl ${highlight.bgPattern} p-8 hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:-rotate-2`}
                style={{ animationDelay: `${index * 200}ms` }}
              >
                <div className="absolute bottom-0 right-0 w-32 h-32 opacity-10">
                  <div
                    className={`w-full h-full bg-gradient-to-br ${highlight.color} rounded-full transform translate-x-8 translate-y-8 group-hover:scale-150 transition-transform duration-700`}
                  ></div>
                </div>

                <div className="relative z-10">
                  <div
                    className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${highlight.color} rounded-2xl mb-6 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110 group-hover:-rotate-12`}
                  >
                    <highlight.icon className="w-8 h-8 text-white" />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-300">
                    {highlight.title}
                  </h3>

                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{highlight.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Creative About Section */}
        <div
          data-animate
          id="about"
          className={`mb-20 transform transition-all duration-1000 delay-400 ${
            visibleSections.has("about") ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0"
          }`}
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-3xl transform rotate-2"></div>
            <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-white/20">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500"></div>

              <div className="p-12">
                <div className="flex flex-col lg:flex-row items-start space-y-8 lg:space-y-0 lg:space-x-12">
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-3xl blur-lg opacity-30"></div>
                      <div className="relative w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-3xl flex items-center justify-center shadow-xl">
                        <FaQuoteLeft className="w-10 h-10 text-white" />
                      </div>
                    </div>
                  </div>

                  <div className="flex-1">
                    <h2 className="text-4xl md:text-5xl font-bold mb-8">
                      <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                        Our Mission
                      </span>
                    </h2>

                    <div className="space-y-6 text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                      <p className="relative pl-6">
                        <span className="absolute left-0 top-2 w-2 h-2 bg-purple-500 rounded-full"></span>
                        The Graphics and Design Committee stands as the artistic backbone of the IIChE Student Chapter,
                        where creativity meets technical excellence in every visual narrative.
                      </p>

                      <p className="relative pl-6">
                        <span className="absolute left-0 top-2 w-2 h-2 bg-pink-500 rounded-full"></span>
                        We specialize in translating the spirit of events into stunning visuals, creating everything
                        from posters and social media graphics to immersive video content.
                      </p>

                      <p className="relative pl-6">
                        <span className="absolute left-0 top-2 w-2 h-2 bg-rose-500 rounded-full"></span>
                        Our collaborative environment fosters continuous learning and innovation, where passionate
                        designers work together to maintain the chapter's visual identity and creative standards.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Creative Team Section */}
        <div
          data-animate
          id="team"
          className={`transform transition-all duration-1000 delay-600 ${
            visibleSections.has("team") ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0"
          }`}
        >
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Meet Our Team
              </span>
            </h2>

            <div className="flex justify-center items-center space-x-4 mb-8">
              <div className="w-20 h-1 bg-gradient-to-r from-purple-500 to-transparent rounded-full"></div>
              <FaMagic className="w-8 h-8 text-purple-500 animate-bounce" />
              <div className="w-20 h-1 bg-gradient-to-l from-pink-500 to-transparent rounded-full"></div>
            </div>

            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Creative minds shaping visual excellence
            </p>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="relative inline-block">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full blur-lg opacity-30 animate-pulse"></div>
                <FaSpinner className="relative w-16 h-16 text-purple-600 animate-spin" />
              </div>
              <p className="text-2xl text-gray-600 dark:text-gray-400 font-medium mt-6">
                Loading our creative artists...
              </p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-8 max-w-md mx-auto">
                <FaExclamationTriangle className="w-16 h-16 text-red-600 dark:text-red-400 mx-auto mb-6" />
                <h3 className="text-2xl font-semibold text-red-800 dark:text-red-200 mb-4">
                  Oops! Something went wrong
                </h3>
                <p className="text-red-700 dark:text-red-300">{error}</p>
              </div>
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-20">
              <div className="relative inline-block mb-8">
                <div className="absolute inset-0 bg-gradient-to-r from-gray-400 to-gray-600 rounded-full blur-lg opacity-20"></div>
                <FaUsers className="relative w-20 h-20 text-gray-400" />
              </div>
              <h3 className="text-3xl font-semibold text-gray-900 dark:text-white mb-4">Team Coming Soon</h3>
              <p className="text-xl text-gray-600 dark:text-gray-400">
                Our creative artists will be showcased here soon!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {members.map((member, index) => (
                <div
                  key={member.id}
                  className="group relative"
                  style={{ animationDelay: `${index * 150}ms` }}
                  onMouseEnter={() => setActiveCard(member.id)}
                  onMouseLeave={() => setActiveCard(null)}
                >
                  <div className="relative overflow-hidden rounded-3xl bg-white dark:bg-gray-800 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:-rotate-2">
                    {/* Animated Background */}
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-pink-500/5 to-rose-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                    {/* Image Section */}
                    <div className="relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent z-10"></div>
                      <img
                        src={member.photoUrl || "/placeholder.svg"}
                        alt={member.name}
                        className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-700"
                        onError={(e) => {
                          e.target.src = "/placeholder.svg"
                        }}
                      />

                      {/* Floating Designation Badge */}
                      <div className="absolute top-6 right-6 z-20">
                        <div
                          className={`px-4 py-2 rounded-full text-sm font-bold shadow-lg backdrop-blur-sm ${
                            member.designation === "Head"
                              ? "bg-gradient-to-r from-purple-500/90 to-pink-500/90 text-white"
                              : member.designation === "Co-Head"
                                ? "bg-gradient-to-r from-blue-500/90 to-cyan-500/90 text-white"
                                : "bg-white/90 text-gray-800"
                          }`}
                        >
                          {member.designation}
                        </div>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="relative z-10 p-8">
                      <div className="flex items-center space-x-4 mb-4">
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full blur-md opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
                          <div className="relative w-14 h-14 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg">
                            <FaDesktop className="w-7 h-7 text-white" />
                          </div>
                        </div>

                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-300">
                            {member.name}
                          </h3>
                          <p className="text-gray-500 dark:text-gray-400 font-medium">Committee Member</p>
                        </div>
                      </div>

                      {/* Animated Underline */}
                      <div className="w-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full group-hover:w-full transition-all duration-500"></div>
                    </div>

                    {/* Hover Overlay Effect */}
                    <div
                      className={`absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none ${activeCard === member.id ? "opacity-100" : ""}`}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(-5deg); }
        }
        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes gradient-x-reverse {
          0%, 100% { background-position: 100% 50%; }
          50% { background-position: 0% 50%; }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-float-delayed { animation: float-delayed 8s ease-in-out infinite; }
        .animate-gradient-x { 
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }
        .animate-gradient-x-reverse { 
          background-size: 200% 200%;
          animation: gradient-x-reverse 3s ease infinite;
        }
        .animate-spin-slow { animation: spin-slow 8s linear infinite; }
      `}</style>
    </div>
  )
}

export default GraphicsandDesign
